// lineVariance.js
// Pure line-pairing + variance logic for invoice↔quote comparison.

const normSku = (s) => String(s || '').trim().toUpperCase()

const tokenize = (s) => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .split(/\s+/)
  .filter(Boolean)

// Jaccard token overlap, 0..1
const descScore = (a, b) => {
  const ta = new Set(tokenize(a))
  const tb = new Set(tokenize(b))
  if (ta.size === 0 || tb.size === 0) return 0
  let inter = 0
  for (const t of ta) if (tb.has(t)) inter++
  return inter / (ta.size + tb.size - inter)
}

const FUZZY_THRESHOLD = 0.5

function buildVariance (invLine, quoteLine, matchType) {
  const qQty = Number(quoteLine.quantity) || 0
  const iQty = Number(invLine.quantity) || 0
  const qPrice = Number(quoteLine.unitPrice) || 0
  const iPrice = Number(invLine.unitPrice) || 0
  return {
    invoiceLine: invLine,
    quoteLine,
    matchType, // 'sku' | 'description' | 'manual'
    quantityChanged: qQty !== iQty,
    unitPriceChanged: qPrice !== iPrice,
    priceChanged: qPrice !== iPrice, // alias
    qtyDelta: iQty - qQty,
    priceDelta: iPrice - qPrice
  }
}

/**
 * Pair invoice line items against quote line items.
 * @param {Array} invoiceLines
 * @param {Array} quoteLines
 * @returns {{ pairs, addedOnInvoice, droppedFromQuote }}
 */
function pairLineItems (invoiceLines = [], quoteLines = []) {
  const pairs = []
  const usedQuoteIdx = new Set()

  // Pass 1: exact SKU
  invoiceLines.forEach((inv, invIdx) => {
    const sku = normSku(inv.vendorSku)
    if (!sku) return
    const qIdx = quoteLines.findIndex((q, i) => !usedQuoteIdx.has(i) && normSku(q.vendorSku) === sku)
    if (qIdx >= 0) {
      usedQuoteIdx.add(qIdx)
      pairs.push({ invIdx, ...buildVariance(inv, quoteLines[qIdx], 'sku') })
    }
  })
  const pairedInvIdx = new Set(pairs.map(p => p.invIdx))

  // Pass 2: fuzzy description for still-unpaired invoice lines
  invoiceLines.forEach((inv, invIdx) => {
    if (pairedInvIdx.has(invIdx)) return
    let best = { idx: -1, score: 0 }
    quoteLines.forEach((q, i) => {
      if (usedQuoteIdx.has(i)) return
      const s = descScore(inv.description, q.description)
      if (s > best.score) best = { idx: i, score: s }
    })
    if (best.idx >= 0 && best.score >= FUZZY_THRESHOLD) {
      usedQuoteIdx.add(best.idx)
      pairedInvIdx.add(invIdx)
      pairs.push({ invIdx, ...buildVariance(inv, quoteLines[best.idx], 'description') })
    }
  })

  const addedOnInvoice = invoiceLines.filter((_, i) => !pairedInvIdx.has(i))
  const droppedFromQuote = quoteLines.filter((_, i) => !usedQuoteIdx.has(i))

  // Keep pairs in invoice order
  pairs.sort((a, b) => a.invIdx - b.invIdx)
  return { pairs, addedOnInvoice, droppedFromQuote }
}

export { pairLineItems, descScore }
